import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { currencySymbol } from "@/lib/currency";

/**
 * CurrencyInput — consistent currency field used across every financial input.
 *
 * - Fixed "$" prefix inside the padding.
 * - Live comma formatting as the user types ("12500.75" → "12,500.75").
 * - Emits the RAW numeric string to `onChange` (no commas), so parents can
 *   `Number(v) || 0` and store as a number.
 * - `inputMode="decimal"` for mobile numeric keyboard.
 * - No native browser stepper controls (uses type="text").
 */

// Strip commas, disallow non-numeric except one dot and a single leading minus.
export function stripCurrency(raw) {
  if (raw === undefined || raw === null) return "";
  let v = String(raw).replace(/,/g, "").replace(/[^\d.\-]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  // Keep leading minus only at position 0
  v = v.replace(/(?!^)-/g, "");
  return v;
}

// Format a raw numeric string with thousand-separators, preserving trailing dot
// and up to typed decimals so mid-typing state ("12500.") isn't lost.
export function formatCurrency(raw) {
  const s = stripCurrency(raw);
  if (s === "" || s === "-") return s;
  const [intPart, decPart] = s.split(".");
  const negative = intPart.startsWith("-");
  const intDigits = negative ? intPart.slice(1) : intPart;
  const withCommas = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const signed = negative ? `-${withCommas}` : withCommas;
  return s.includes(".") ? `${signed}.${decPart || ""}` : signed;
}

export default function CurrencyInput({ value, onChange, placeholder = "0.00", className = "", currency = "USD", ...rest }) {
  const symbol = currencySymbol(currency);
  const [display, setDisplay] = useState(() => formatCurrency(value));
  const lastEmittedRef = useRef(stripCurrency(value));

  // Keep display in sync with external value changes (initial load, resets, etc.)
  useEffect(() => {
    const raw = stripCurrency(value);
    if (raw !== lastEmittedRef.current) {
      lastEmittedRef.current = raw;
      setDisplay(formatCurrency(raw));
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = stripCurrency(e.target.value);
    lastEmittedRef.current = raw;
    setDisplay(formatCurrency(raw));
    onChange?.(raw);
  };

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm select-none">
        {symbol}
      </span>
      <Input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={display}
        onChange={handleChange}
        placeholder={placeholder}
        className={`pl-8 ${className}`}
        {...rest}
      />
    </div>
  );
}
